import React, { useState } from 'react';
import { FormInstance } from 'antd/es/form';
import { UploadChangeParam } from 'antd/lib/upload/interface';
import Configs from 'config';
import VerificationCompanyModal from 'components/modal/VerificationCompanyModal';
import { sendVerificationEmail, submitVerification } from 'libs/api/user';
import { filteredFileNames } from 'libs/utils';
import { TVerificationEmail, TSubmitVerification } from 'modules/user';
import { CUploadFileProps } from 'constants/base.const';
import { CUserType, CUserTypeColor } from 'constants/user.const';
import { EUserType, EEmailType } from 'enums/base.enum';
import { Spin, Button, Form, Typography, Input, Tag, Upload, message } from 'antd';
import { CheckOutlined, CheckSquareOutlined } from '@ant-design/icons';
import { FormWrapper } from 'GlobalStyles';
import { PrimaryColor } from 'GlobalStyles';

const { Text, Title } = Typography;
type TVerificationProps = {
  userType?: EUserType;
  form: FormInstance;
  menteeId?: string;
  buttonText?: string;
  onFinish?: () => void;
};

const Verification = ({ userType, form, menteeId, buttonText, onFinish }: TVerificationProps) => {
  const [verificationModal, setVerificationModal] = useState(false);
  const [uploadPensionVisible, setUploadPensionVisible] = useState(true);
  const [uploadInsuranceVisible, setUploadInsuranceVisible] = useState(true);
  const [emailButtonDisabled, setEmailButtonDisabled] = useState(false);
  const [sendWorkerEmail, setSendWorkerEmail] = useState(false);
  const [sendCoworkerEmail, setSendCoworkerEmail] = useState(false);
  const [submitButtonLoading, setSubmitButtonLoading] = useState(false);

  async function sendEmail(emailType: EEmailType) {
    try {
      setEmailButtonDisabled(true);
      await sendVerificationEmail({
        for_userid: menteeId ? menteeId : form.getFieldValue('userid'),
        send_email_to: form.getFieldValue(`${emailType}_email`),
        email_type: emailType,
      } as TVerificationEmail);
      message.info('인증 이메일을 보냈습니다.');
      if (emailType === EEmailType.Worker) {
        setSendWorkerEmail(true);
      } else {
        setSendCoworkerEmail(true);
      }
    } catch (e) {
      throw e;
    } finally {
      setEmailButtonDisabled(false);
    }
  }

  return (
    <Spin tip='loading...' spinning={emailButtonDisabled}>
      <FormWrapper style={{ maxWidth: '380px' }}>
        <Form name='verification' form={form} layout='vertical' className='form-inline'>
          {userType && <Tag color={CUserTypeColor[userType]}>{CUserType[userType]}</Tag>}
          <Title level={2}>
            <span style={{ color: `${PrimaryColor}` }}>전·현직 인증</span>을
            <br />
            진행해주세요
          </Title>
          <Button
            style={{ marginLeft: '3px' }}
            size='middle'
            onClick={() => {
              setVerificationModal(true);
            }}
          >
            현재 인증 가능 회사
          </Button>
          <div
            style={{
              fontSize: '14px',
              marginTop: '30px',
              marginBottom: '8px',
              color: '#000',
            }}
          >
            <b>현직 인증하기</b>
          </div>
          <Form.Item
            name={`${EEmailType.Worker}_email`}
            className='form-item'
            label='업무용 이메일'
            rules={[
              {
                type: 'email',
                message: '이메일 형식이 올바르지 않습니다',
              },
            ]}
          >
            <Input
              size='large'
              placeholder='업무용 이메일 주소를 입력해주세요'
              suffix={
                sendWorkerEmail === false ? (
                  <Button
                    className='btn-text'
                    disabled={emailButtonDisabled}
                    onClick={() => {
                      form.validateFields([`${EEmailType.Worker}_email`]).then(() => {
                        sendEmail(EEmailType.Worker);
                      });
                    }}
                  >
                    인증메일 발송
                  </Button>
                ) : (
                  <div>
                    <CheckOutlined />
                    발송 완료
                  </div>
                )
              }
            />
          </Form.Item>
          <div
            style={{
              fontSize: '14px',
              marginTop: '30px',
              marginBottom: '8px',
              color: '#000',
            }}
          >
            <b>전직 인증하기</b>
            <br />
            <span style={{ fontSize: '12px' }}>
              아래 세가지 방법 중 한가지 방법으로 전직임을 증명 부탁 드립니다.
            </span>
            <Button size='small'>
              <a href={`${require('assets/documents/how_to_verify.pdf')}`} target='_blank'>
                전직인증하기 안내
              </a>
            </Button>
          </div>
          <Form.Item
            name={'verification_national_pension'}
            className='form-item'
            shouldUpdate={(prevValues, currentValues) => {
              return prevValues.verification_national_pension !== currentValues.verification_national_pension;
            }}
            valuePropName='fileList'
            label='국민연금 가입 증명서'
            getValueFromEvent={(e: UploadChangeParam) => (Array.isArray(e) ? e : e && e?.fileList)}
          >
            <Upload
              name='file'
              {...CUploadFileProps}
              action={`${Configs.API_HOST}/verification/pension`}
              onChange={(info: UploadChangeParam) => {
                const { status } = info.file;

                if (status === 'done') {
                  message.success(`${info.file.name} 업로드에 성공했습니다.`);
                  setUploadPensionVisible(false);
                } else if (status === 'error') {
                  message.error(`${info.file.name} 업로드에 실패했습니다.`);
                } else if (status === 'removed') {
                  if (info.fileList.length === 0) {
                    setUploadPensionVisible(true);
                  }
                }
              }}
            >
              {uploadPensionVisible === true && (
                <div style={{ float: 'right' }} className='input-suffix'>
                  업로드
                </div>
              )}
            </Upload>
          </Form.Item>
          <Form.Item
            name={'verification_health_insurance'}
            className='form-item'
            shouldUpdate={(prevValues, currentValues) => {
              return prevValues.verification_health_insurance !== currentValues.verification_health_insurance;
            }}
            valuePropName='fileList'
            label='건강보험 자격득실 확인서'
            getValueFromEvent={(e: UploadChangeParam) => (Array.isArray(e) ? e : e && e?.fileList)}
          >
            <Upload
              name='file'
              {...CUploadFileProps}
              action={`${Configs.API_HOST}/verification/insurance`}
              onChange={(info: UploadChangeParam) => {
                const { status } = info.file;

                if (status === 'done') {
                  message.success(`${info.file.name} 업로드에 성공했습니다.`);
                  setUploadInsuranceVisible(false);
                } else if (status === 'error') {
                  message.error(`${info.file.name} 업로드에 실패했습니다.`);
                } else if (status === 'removed') {
                  if (info.fileList.length === 0) {
                    setUploadInsuranceVisible(true);
                  }
                }
              }}
            >
              {uploadInsuranceVisible === true && (
                <div style={{ float: 'right' }} className='input-suffix'>
                  업로드
                </div>
              )}
            </Upload>
          </Form.Item>
          <div
            style={{
              fontSize: '12px',
              marginTop: '20px',
              marginBottom: '10px',
              color: '#000',
            }}
          >
            <span style={{ color: '#FBBC05' }}>
              <CheckSquareOutlined />
            </span>
            <br />
            동일 회사 동료였던 현직 분이 대신 인증해드립니다.
            <br />
            (본인의 근무경력을 인증하실 수 있는 현직 동료분에게 메일을 발송합니다. 해당 현직분이 대신 인증해
            드립니다.)
          </div>
          <Form.Item
            name={`${EEmailType.Coworker}_email`}
            className='form-item'
            label='동료 업무용 이메일'
            rules={[
              {
                type: 'email',
                message: '이메일 형식이 올바르지 않습니다',
              },
            ]}
          >
            <Input
              size='large'
              placeholder='동료 업무용 이메일 주소를 입력해주세요'
              suffix={
                sendCoworkerEmail === false ? (
                  <Button
                    className='btn-text'
                    disabled={emailButtonDisabled}
                    onClick={() => {
                      form.validateFields([`${EEmailType.Coworker}_email`]).then(() => {
                        sendEmail(EEmailType.Coworker);
                      });
                    }}
                  >
                    인증메일 발송
                  </Button>
                ) : (
                  <div>
                    <CheckOutlined />
                    발송 완료
                  </div>
                )
              }
            />
          </Form.Item>
          <Form.Item shouldUpdate={true} noStyle={true}>
            {() => (
              <div className='steps-action'>
                <Button
                  type='primary'
                  loading={submitButtonLoading}
                  size='large'
                  onClick={async () => {
                    try {
                      setSubmitButtonLoading(true);
                      const response = await submitVerification({
                        for_userid: menteeId ? menteeId : form.getFieldValue('userid'),
                        verification_pension_ids: filteredFileNames(
                          form.getFieldValue('verification_national_pension'),
                        ),
                        verification_insurance_ids: filteredFileNames(
                          form.getFieldValue('verification_health_insurance'),
                        ),
                      } as TSubmitVerification);
                    } catch (e) {
                      throw e;
                    } finally {
                      setSubmitButtonLoading(false);
                    }
                    onFinish && onFinish();
                  }}
                >
                  {buttonText ? buttonText : '다음'}
                </Button>
              </div>
            )}
          </Form.Item>
        </Form>
        <VerificationCompanyModal visible={verificationModal} setVisible={setVerificationModal} />
      </FormWrapper>
    </Spin>
  );
};

export default Verification;
